import { BaseViewModel } from "@iot-platform/core";
import type { LatestData } from "@station/domain/models/latest-data.model.ts";
import type { Station } from "@station/domain/models/station.model";
import { LatestDataGetAllUseCase } from "@station/domain/usecases/latest-data-get-all.usecase.ts";
import { SpatialLevelRefreshStationUseCase } from "@station/domain/usecases/spatial-level-refresh-station.usecase.ts";
import { StationGetSelectedUseCase } from "@station/domain/usecases/station-get-selected.usecase";
import { StationRemoveSelectedUseCase } from "@station/domain/usecases/station-remove-selected.usecase";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

@injectable()
export abstract class StationDetailBaseViewmodel extends BaseViewModel {
    @observable
    selectedStations: readonly Readonly<Station>[] = [];

    @observable
    latestDataDictionary = new Map<number, LatestData[]>();

    @observable
    private readonly inFlightStationIds = new Set<string>();

    @computed
    get latestDataLoading(): boolean {
        return this.inFlightStationIds.size > 0;
    }

    constructor(
        @inject(StationGetSelectedUseCase)
        private readonly stationGetSelectedUseCase: StationGetSelectedUseCase,
        @inject(StationRemoveSelectedUseCase)
        private readonly stationRemoveSelectedUseCase: StationRemoveSelectedUseCase,
        @inject(LatestDataGetAllUseCase)
        private readonly latestDataGetAllUseCase: LatestDataGetAllUseCase,
        @inject(SpatialLevelRefreshStationUseCase)
        private readonly spatialLevelRefreshStationUseCase: SpatialLevelRefreshStationUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    override didMount(): void {
        this.addSub(
            this.stationGetSelectedUseCase.execute().subscribe((stations) => {
                this.onSelectedStationsChanged(stations);
            }),
        );
    }

    @action
    fetchLatestData(): void {
        for (const station of this.selectedStations) {
            void this.fetchLatestDataDictionary(station.id.value);
        }
    }

    @action
    removeStation(station: number): void {
        this.stationRemoveSelectedUseCase.execute(station);
        runInAction(() => {
            this.latestDataDictionary.delete(station);
        });
    }

    @action
    private onSelectedStationsChanged(
        stations: readonly Readonly<Station>[],
    ): void {
        const previousIds = new Set(
            this.selectedStations.map((station) => station.id.value),
        );
        const nextIds = new Set(stations.map((station) => station.id.value));

        this.selectedStations = stations;

        for (const previousId of previousIds) {
            if (!nextIds.has(previousId)) {
                this.latestDataDictionary.delete(Number(previousId));
            }
        }

        const newStations = stations.filter(
            (station) => !previousIds.has(station.id.value),
        );

        for (const station of newStations) {
            void this.fetchLatestDataDictionary(station.id.value);
        }
    }

    private async fetchLatestDataDictionary(stationId: string): Promise<void> {
        if (this.inFlightStationIds.has(stationId)) {
            return;
        }
        runInAction(() => {
            this.inFlightStationIds.add(stationId);
        });

        try {
            const latestData =
                await this.latestDataGetAllUseCase.execute(stationId);

            const latestDataTimestamp = Math.max(
                ...latestData.map((lData) => lData.timeStamp),
            );
            const latestDataTimestampDate =
                DateTime.fromSeconds(latestDataTimestamp);

            const station = this.selectedStations.find(
                (selectedStation) => selectedStation.id.value === stationId,
            );

            const refreshResult = latestDataTimestampDate.isValid
                ? station?.refreshLastFrame(latestDataTimestampDate)
                : undefined;

            runInAction(() => {
                if (refreshResult?.isOk()) {
                    this.selectedStations = this.selectedStations.map((stn) =>
                        stn.id.value === stationId ? refreshResult.value : stn,
                    );
                    this.spatialLevelRefreshStationUseCase.execute(
                        refreshResult.value,
                    );
                }
                this.latestDataDictionary.set(Number(stationId), latestData);
            });
        } finally {
            runInAction(() => {
                this.inFlightStationIds.delete(stationId);
            });
        }
    }
}
